<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'
import {
  RIGHT_PANEL_TOAST_VIEWPORT_PRESET_FLOATING,
  useToastViewportScope,
} from '@/composables/toast'
import { useI18n } from 'vue-i18n'
import { variableTypeOptions } from 'shared/src'
import { useVariablesAllQuery } from '@/composables/queries/variables'
import { useConfigurator } from '@/composables/configurator/useConfigurator'
import { extractMentions } from '@/utils/String'

const { t } = useI18n()

const open = ref(false)
const isCopied = ref(false)

useToastViewportScope(open, RIGHT_PANEL_TOAST_VIEWPORT_PRESET_FLOATING)

const { data, isLoading } = useVariablesAllQuery()
const {
  generalInformation,
  conversationFlow,
  customerIntents,
  inactivity,
  callDurationLimits,
  knowledge,
  experimental,
} = useConfigurator()

const variables = computed(() => data.value ?? [])

const typeLabels = Object.fromEntries(variableTypeOptions.map((o) => [o.value, o.label]))

const listColumns = computed(() => [
  { key: 'name', label: t('name') },
  { key: 'type', label: t('variables.type') },
  { key: 'fallbackValue', label: t('variables.fallback_value') },
  { key: 'description', label: t('description') },
])

const usedVariableNames = computed(() => {
  const names = new Set<string>()
  const add = (text: string | undefined | null) => {
    extractMentions(text).forEach((n) => names.add(n))
  }

  add(generalInformation.value.agent_description)
  add(experimental.value.contextual_prompt_override)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  conversationFlow.value.opening_phrases?.forEach((p) => {
    add(typeof p === 'string' ? p : p.opening_phrase)
  })
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  conversationFlow.value.closing_phrases?.forEach((p) => {
    add(typeof p === 'string' ? p : p.closing_phrase)
  })
  add(inactivity.value.re_engagement_description)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  callDurationLimits.value.max_call_duration_speech?.forEach((p) => {
    add(p.message)
  })
  add(knowledge.value.custom_output)
  add(knowledge.value.custom_output_unsuccessful)
  customerIntents.value.forEach((intent) => {
    add(intent.description)
    intent.training_phrases?.forEach((p) => {
      add(p)
    })
  })

  return [...names].sort()
})

const variablesMap = computed(() =>
  Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (data.value ?? []).map((v) => [v.name, v.fallbackValue?.trim() ? v.fallbackValue : null]),
  ),
)

const jsonPayload = computed(() => ({
  variables: Object.fromEntries(
    usedVariableNames.value.map((n) => [n, variablesMap.value[n] ?? null]),
  ),
}))

const formattedJson = computed(() => JSON.stringify(jsonPayload.value, null, 2))

const openPanel = () => {
  open.value = true
}

let copyResetTimer: ReturnType<typeof setTimeout> | null = null

const copyJson = async () => {
  await navigator.clipboard.writeText(formattedJson.value)
  isCopied.value = true
  if (copyResetTimer) clearTimeout(copyResetTimer)
  copyResetTimer = setTimeout(() => {
    isCopied.value = false
  }, 2000)
}

onUnmounted(() => {
  if (copyResetTimer) clearTimeout(copyResetTimer)
})

defineExpose({ openPanel })
</script>

<template>
  <div class="variable-dictionary-panel">
    <ORightPanel
      v-model:open="open"
      :title="t('variables.dictionary_title')"
      floating
      panel-class="right-4"
      content-class="pr-0"
    >
      <template #default>
        <div class="flex flex-col h-full">
          <div class="pb-l space-y-s shrink-0">
            <p class="typography-body-1-medium text-neutral-1">
              {{ t('variables.available_title') }}
            </p>
            <p class="typography-body-2-long text-neutral-1 mt-1">
              {{ t('variables.available_description') }}
            </p>
          </div>
          <div class="flex-1 min-h-[300px]">
            <OTable :columns="listColumns" :modelValue="variables" :loading="isLoading">
              <template #type="{ item }">
                {{ typeLabels[item.type] ?? item.type }}
              </template>
            </OTable>
          </div>

          <div class="pb-l space-y-s mt-xl shrink-0">
            <p class="typography-body-1-medium text-neutral-1">
              {{ t('variables.used_in_config_title') }}
            </p>
            <p class="typography-body-2-long text-neutral-1 mt-1">
              {{ t('variables.used_in_config_description') }}
            </p>
          </div>
          <div
            class="flex-1 min-h-[200px] relative border radius-l border-neutral-4 bg-neutral-6 overflow-hidden flex flex-col"
          >
            <button
              data-testid="copy-json-btn"
              class="absolute top-3 right-3 text-primary cursor-pointer"
              :aria-label="t(isCopied ? 'copied' : 'copy')"
              @click="copyJson"
            >
              <UiIcon :name="isCopied ? 'positive-check' : 'copy'" variant="solid" />
            </button>
            <pre
              class="flex-1 min-h-0 p-3 pr-10 overflow-auto scrollbar-s-grey-bordered typography-body-2 text-[#706E7B]! leading-6"
              >{{ formattedJson }}</pre>
          </div>
        </div>
      </template>
    </ORightPanel>
  </div>
</template>

<style>
.variable-dictionary-panel [data-testid='right-panel'] {
  width: 800px !important;
}
.variable-dictionary-panel [data-testid$='-scroll-container'] {
  overflow-x: hidden;
}
</style>
